'use strict'

import '../../../assets/css/style.css'

import * as React from 'react'
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom'
import { useSelector, useStore } from 'react-redux'
import { Link } from '@mui/material'
import { SpinnerButton } from '../../common/CustomButton'
import { arrayMove } from '@dnd-kit/sortable'
import { errorLog } from '../../../common/logger/Logger'
import { viewId, titleName, masterClassification } from '../../../_constants/Code'
import {
  checkIfCorporationNumberInUse,
  fetchGroupCompanyInfoList,
  fetchMaster,
  updateGroupCompanyInfo
} from './extra/services'
import { useToast } from '../../../_components/Toast'
import { useModal, useModalExtra } from '../../../_components/Modal'
import RelatedDashboard from '../parts/RelatedDashboard'
import { GroupCompanyInfoModal } from './components/GroupCompanyInfoModal'
import { useModifyClassList as modifyClassList } from '../../../common/utils/CSSUtils'
import { produce } from 'immer'
import { getMessage } from '../../../common/utils/MessageUtils'
import Forbidden from '../../../_components/Forbidden'
import { startOfMonth, endOfMonth, isAfter, formatDate as _formatDate } from 'date-fns'
import { ClientError } from '../../../common/utils/Exception'
import {
  checkIfCompanyValid,
  checkIfDuplicateCompName,
  checkIfDuplicateCompNumber,
  checkIfGroupCompanyInUse,
  checkIfValidPeriodCorrect,
  convertResponseToState
} from './extra/helpers'
import { GroupCompanyTable } from './components/GroupCompanyInfoTable'

const groupCompanyInfoReducer = (state, action) => {
  return produce(state, (draft) => {
    const { type, payload } = action

    switch (type) {
      case 'INIT_STATE': {
        Object.assign(draft, payload)
        break
      }

      case 'ADD_GROUP_COMPANY_INFO_LIST': {
        const { corporationNumber, compName, compType, validStartDate, validEndDate, memo = '' } = payload

        const groupCount = draft.groupCompanyList.length
        const allDisplayOrder = draft.groupCompanyList.map((e) => Number(e.displayOrder || groupCount))

        const maxDisplayOrder = Math.max(0, ...allDisplayOrder)

        draft.groupCompanyList.push({
          operationType: 'create',
          key: crypto.randomUUID(),
          corporationNumber,
          compName: String(compName).trim(),
          compType,
          validStartDate: !!validStartDate ? startOfMonth(validStartDate) : null,
          validEndDate: !!validEndDate ? endOfMonth(validEndDate) : null,
          displayOrder: maxDisplayOrder + 1,
          memo: String(memo).trim(),
          isDeleted: false,
          isRegistered: false
        })
        break
      }

      case 'EDIT_GROUP_COMPANY_INFO_LIST': {
        const { key, validStartDate, validEndDate } = payload
        const item = draft.groupCompanyList.find((e) => e.key === key)
        if (!item) {
          console.log('グループ会社見つかりません')
          break
        }
        const { isRegistered } = item

        Object.assign(item, {
          ...payload,
          compName: String(payload?.compName).trim(),
          memo: (payload?.memo ?? '').trim(),
          operationType: isRegistered ? 'update' : 'create',
          validStartDate: !!validStartDate ? startOfMonth(validStartDate) : null,
          validEndDate: !!validEndDate ? endOfMonth(validEndDate) : null
        })
        break
      }

      case 'DELETE_GROUP_COMPANY_INFO_LIST': {
        const { key, restore } = payload
        const groupCompanyList = draft.groupCompanyList

        const item = groupCompanyList.find((e) => e.key === key)

        if (!item) break

        if (item.isRegistered) {
          // 確認 変更後の削除に変更する化初期状態に戻すか

          if (!restore) {
            // 削除
            item.isDeleted = true
            item.previousOperationType = item.operationType
            item.operationType = 'delete'
          } else {
            // 復元
            item.isDeleted = false
            item.operationType = item.previousOperationType
          }
        } else {
          // 削除
          draft.groupCompanyList.splice(groupCompanyList.indexOf(item), 1)
        }

        break
      }

      case 'TOGGLE_LOAD_STATE': {
        draft.isLoaded = !draft.isLoaded
        break
      }

      case 'UPDATE_DISPLAY_ORDER': {
        const { active, over } = payload
        draft.hasDisplayOrderChanged = true
        if (active.id === over.id) break

        const oldIndex = draft.groupCompanyList.findIndex((e) => e.key === active.id)
        const newIndex = draft.groupCompanyList.findIndex((e) => e.key === over.id)

        const newDisplayOrderList = arrayMove(draft.groupCompanyList, oldIndex, newIndex)

        const updatedDisplayOrder = newDisplayOrderList.map((e, index) => ({
          ...e,
          displayOrder: index + 1,
          operationType: !e.isRegistered ? 'create' : 'update'
        }))

        draft.groupCompanyList = updatedDisplayOrder

        break
      }
      case 'ERROR': {
        if (!payload.data) {
          draft.inputError.errorMessages = [getMessage('E011012')]
          break
        }
        const { messages = [], errorCorporationNumber = '' } = payload?.data

        draft.inputError.errorMessages = messages || []

        if (errorCorporationNumber === null) break

        const indexes = draft.groupCompanyList.reduce((acc, curr, idx) => {
          if (curr.corporationNumber === errorCorporationNumber) acc.push(idx)
          return acc
        }, [])

        indexes.forEach((e) => {
          draft.groupCompanyList[e].isError = true
        })
        break
      }

      default:
        break
    }
  })
}

export const useRDispatch = (dispatch) => React.useCallback((type, payload) => dispatch({ type, payload }), [dispatch])
/**
 * @function GroupCompanyInfoContent
 * @type {React.FC}
 */
export const GroupCompanyInfoContent = (props) => {
  const initialPageState = {
    isLoaded: false,
    master: [],
    groupCompanyList: [],
    inputError: { errorMessages: [] },
    hasDisplayOrderChanged: false
  }

  const [pageState, _dispatch] = React.useReducer(groupCompanyInfoReducer, initialPageState)
  const dispatch = useRDispatch(_dispatch)
  const store = useStore()

  const { isLoaded, master, groupCompanyList, inputError } = pageState
  const hasGroupCompany = !!groupCompanyList.length

  const init = async () => {
    // マスター情報取得
    // セグメント情報取得
    const [master, groupCompanyInfoList] = await Promise.all([fetchMaster(store), fetchGroupCompanyInfoList(store)])

    dispatch('INIT_STATE', {
      isLoaded: true,
      master,
      groupCompanyList: groupCompanyInfoList.map(convertResponseToState)
    })
  }

  /** @function openConfirmUpdateModal データ更新モダルを表示 */
  async function openConfirmUpdateModal() {
    try {
      await validateGroupCompanyList(groupCompanyList)

      const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

      await promisify((res) => {
        props.showModal({
          title: 'マスター設定登録の確認',
          onClose: res,
          isWide: false,
          description: (
            <>
              <p>
                設定値を登録してダッシュボードに反映します。<span className="whitespace-nowrap">よろしいですか</span>
              </p>
              <p>※ダッシュボードへの反映には5分～10分程度かかります</p>
              <p>マスター管理画面のステータスから反映状況を確認してください</p>
            </>
          ),
          button: [
            {
              name: '実行',
              className: 'footer-btn button_base',
              click: () => _updateGroupCompanyInfo(groupCompanyList)
            }
          ]
        })
      })
    } catch (error) {
      dispatch('ERROR', error)
    }
  }

  /**
   * @function validateGroupCompanyList
   * @param {GroupCompanyInfo[]} groupCompanyList
   */
  const validateGroupCompanyList = async (groupCompanyList) => {
    const errors = []
    let errCorporationNumber = null
    for (const company of groupCompanyList) {
      const { validStartDate, validEndDate, corporationNumber, operationType, compName } = company

      if (validStartDate !== null && validEndDate !== null) {
        const validStartDateOfMonth = startOfMonth(validStartDate)
        const validEndDateOfMonth = endOfMonth(validEndDate)

        // 日付チェック
        if (isAfter(validStartDateOfMonth, validEndDateOfMonth)) {
          // 　[有効開始月] <= [有効終了月]
          errors.push(getMessage('E011013', '有効終了月', '有効開始月'))
        }
      }

      const inUse = company.isInUse

      if (inUse && operationType === 'delete') {
        // 　[有効開始月] <= [有効終了月]
        errors.push(`「${compName}」は有価証券報告書の集計対象に設定されているため、削除できません。`)
      }
    }

    if (errors.length) {
      throw new ClientError({
        messages: errors,
        errorCorporationNumber: errCorporationNumber
      })
    }

    return true
  }

  /**
   * 更新処理
   *
   * @function _updateGroupCompanyInfo
   * @param {GroupCompanyInfo[]} groupCompanyList
   */
  const _updateGroupCompanyInfo = async (groupCompanyList) => {
    try {
      dispatch('ERROR', { data: { messages: [] } })

      await updateGroupCompanyInfo(store, groupCompanyList)

      // 更新
      props.navigate('/master/masterList', { state: { message: 'グループ会社設定を保存しました。' } })
    } catch (error) {
      console.log(error)
      errorLog('E000001', props.userId, viewId.groupCompanyInfo, 'groupCompanyInfo', error)

      dispatch('ERROR', error)
    }
  }

  /** @function openCompanyAddModal 会社追加処理 */
  async function openCompanyAddModal({ create }) {
    const key = Math.random()

    let formData = null
    let setErrors = null

    const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

    /** @function handleFormChange */
    const handleFormChange = (data, actions) => {
      const { setErrors: _setErrors } = actions
      formData = data
      setErrors = _setErrors
    }

    await promisify((res) => {
      props.showModal({
        title: 'グループ会社設定',
        onClose: res,
        isWide: false,
        body: <GroupCompanyInfoModal key={key} onChange={handleFormChange} />,
        button: [
          {
            name: '設定',
            className: 'footer-btn button_base',
            close: false,
            click: async () => {
              try {
                await create(formData)
                res()
              } catch (error) {
                console.error(error)
                if (setErrors !== null) setErrors(error.data)
              }
            }
          }
        ]
      })
    }).finally(() => {
      props.modalExtra._closeUpModal()
    })
  }

  /**
   * @function addToGroupCompanyInfoList 一覧に登録
   * @param {TableData} formData
   */
  async function addToGroupCompanyInfoList(formData) {
    const [isError, errors] = validateForm(formData, groupCompanyList)
    if (isError) throw new ClientError(errors)

    dispatch('ADD_GROUP_COMPANY_INFO_LIST', formData)
  }

  const GroupCompanyInfoNotRegistered = () => (
    <div className="text-center my-10 pt-10">
      <span className="text-h5 red--text">グループ会社設定が未登録です。</span>
    </div>
  )

  // 入力エラー時にスクロールをトップへ
  React.useEffect(() => {
    if (inputError.errorMessages?.length) {
      window.scrollTo(0, 0)
    }
  }, [inputError])

  React.useEffect(() => {
    document.title = props.pageTitle
    init()
  }, [])

  /** 描画処理 */
  if (!isLoaded) {
    return null
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification[master.Classification].name}
              </h1>
            </header>
            <div className="page-body">
              <div>
                {inputError?.errorMessages?.map((e, index) => (
                  <p key={index} style={{ color: 'red' }}>
                    {e}
                  </p>
                ))}
              </div>
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  グループ会社設定
                </h2>
                <React.Fragment>
                  <div className="flex items-center justify-between my-6">
                    CHROFY利用なしのグループ会社は会社を追加ボタンから登録してください。
                    <br />
                    ここで登録した会社は、有価証券報告書レポートの集計対象として選択できます。
                    <br />
                    有効期限切れの会社は過去データとして保持されますが、以降の集計には含めることができません。
                    <br />
                  </div>
                  <>
                    <div className="flex justify-between items-center base-table-header mb-5">
                      <div className=" grow ">
                        <CompanyStats hasGroupCompany={hasGroupCompany} companyList={groupCompanyList} />
                      </div>

                      <div className="btn-group  border-l border-blue2-lighten-20 pl-5">
                        <button
                          className="header-btn !p-0 "
                          color="inherit"
                          onClick={() => openCompanyAddModal({ create: addToGroupCompanyInfoList })}
                        >
                          <span className="header-btn white--text py-1.5 pl-7 pr-10 inline-flex items-center">
                            <span className="icon-plus-line icon-32px mr-1.5 my-px"></span>
                            <span className="-translate-y-px text-h7">会社を追加</span>
                          </span>
                        </button>
                      </div>
                    </div>
                  </>

                  {hasGroupCompany && (
                    <GroupCompanyTable
                      groupCompanyList={groupCompanyList}
                      dispatch={dispatch}
                      inputError={inputError}
                    />
                  )}
                  {!hasGroupCompany && <GroupCompanyInfoNotRegistered />}
                </React.Fragment>
                <div className="content-footer my-8">
                  <div className="border-t border-blue1" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        キャンセル
                      </button>
                    </Link>
                    {groupCompanyList.length !== 0 && (
                      <SpinnerButton name="登録" className="footer-btn button_base" onClick={openConfirmUpdateModal} />
                    )}
                  </div>
                </div>
              </div>

              {master?.RelatedDashboard && (
                <RelatedDashboard
                  dashboards={[...master?.RelatedDashboard]}
                  authority={props.authority}
                  mode={props.mode}
                />
              )}
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

/**
 * @function formErrors バリデーションチェック
 * @param {TableData} formData
 * @param {GroupCompanyInfo[]} groupCompanyList
 */
export function validateForm(formData, groupCompanyList = [], editMode = null) {
  const checkIfCharsExist = (input = '', chars = []) => chars.some((e) => String(input)?.includes(e))

  const errors = {
    corporationNumber: [],
    compName: [],
    compType: [],
    validStartDate: [],
    validEndDate: [],
    memo: []
  }

  const { corporationNumber, compName, validStartDate } = formData

  // 必須チェック
  if (corporationNumber === '') errors.corporationNumber.push(getMessage('E010001', '法人番号'))
  // 半角と１３桁のチェック
  else if (!/^\d+$/.test(corporationNumber) || corporationNumber.length !== 13) {
    errors.corporationNumber.push('法人番号は13桁の半角数字で入力してください')
  }

  // 必須チェック
  if (compName === '') errors.compName.push(getMessage('E010001', '会社名'))

  // 入力禁止
  const disallowed = [',', '\\', '"']
  if (checkIfCharsExist(compName, disallowed)) {
    errors.compName.push(`次の記号（${disallowed.join(' ')}）は入力できません。`)
  }

  if (!validStartDate) {
    errors.validStartDate.push(getMessage('E010001', '有効開始月'))
  }

  const [isValidPeriodError, validPeriodError] = checkIfValidPeriodCorrect(formData, groupCompanyList, editMode)
  if (isValidPeriodError) errors.validStartDate.push(...validPeriodError)

  const [isDuplicateCompNumberErrors, duplicateCompNumberErrors] = checkIfDuplicateCompNumber(
    groupCompanyList,
    formData,
    editMode
  )

  if (isDuplicateCompNumberErrors) errors.corporationNumber.push(...duplicateCompNumberErrors)

  const [isDuplicateCompNameErrors, duplicateCompNameErrors] = checkIfDuplicateCompName(
    groupCompanyList,
    formData,
    editMode
  )
  if (isDuplicateCompNameErrors) errors.compName.push(...duplicateCompNameErrors)

  const hasError = Object.values(errors).some((e) => e.length > 0)

  return [hasError, errors]
}

/**
 * @function CompanyStats
 * @type {React.FC<typeof props>} 集計情報
 * @param {Object} props
 * @param {boolean} props.hasGroupCompany
 * @param {GroupCompanyInfo[]} props.companyList
 */
export const CompanyStats = (props) => {
  const { hasGroupCompany, companyList = [] } = props

  const [
    validPeriodCompanyCount = 0,
    inValidPeriodCompanyCount = 0,
    chrofyEnabledCompanyCount = 0,
    chrofyDisabledCompanyCount = 0
  ] = React.useMemo(() => {
    let valid = 0
    let invalid = 0
    let chrofyEnabled = 0
    let chrofyDisabled = 0

    for (const { validStartDate, validEndDate, isChrofyEnabled } of companyList) {
      if (isChrofyEnabled) chrofyEnabled++
      else chrofyDisabled++

      if (checkIfCompanyValid(validStartDate, validEndDate)) valid++
      else invalid++
    }

    return [valid, invalid, chrofyEnabled, chrofyDisabled]
  }, [companyList])

  if (!hasGroupCompany) return null

  return (
    <div className="font-bold">
      有効会社数：{validPeriodCompanyCount}社（CHROFY利用あり[
      <CheckIcon />
      ]：{chrofyEnabledCompanyCount}社、利用なし：{chrofyDisabledCompanyCount}社）、無効会社数：
      {inValidPeriodCompanyCount}社
    </div>
  )
}

export const CheckIcon = ({ className }) => <span className={`text-green-500 ${className} `}>✔</span>

const GroupCompanyInfo = () => {
  const state = useSelector((s) => s)
  const location = useLocation()

  const props = {
    pageTitle: titleName.groupCompanyInfo + titleName.common,
    corporationNumber: state.company.corporationNumber,
    corporationName: state.company.name,
    userName: state.user.userName,
    userId: state.user.sub,
    isParent: state.company.isParent,
    message: location.state?.message,
    errorMessages: location.state?.errorMessages,
    authority: state.authority,
    mode: state.analysis.mode,
    navigate: useNavigate(),
    showToast: useToast(),
    showModal: useModal(),
    modalExtra: useModalExtra()
  }

  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Group.GroupCompanyInfo_Set
  ) {
    return <Forbidden />
  }

  return <GroupCompanyInfoContent {...props} />
}

export default GroupCompanyInfo
